//changed due to package upgrade
import React, { useEffect, useState } from 'react';
import Modal from '@mui/material/Modal';
import IntlMessages from '@projectK/helpers/IntlMessages';
import Typography from '@mui/material/Typography';
import AppTextField from '@projectK/components/AppTextField';
import AppGridContainer from '@projectK/components/AppGridContainer';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import jwtAxios from '@projectK/services/auth/JWT';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  alpha,
  Box,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Stack,
  Button,
  Grid,
  FormHelperText,
  TextField,
  Autocomplete,
  IconButton,
} from '@mui/material';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { format } from 'date-fns';
import AppModalConfirmation from '@projectK/components/AppModalConfirmation';
import { CloseRounded } from '@mui/icons-material';
import PropTypes from 'prop-types';
const validationSchema = yup.object({
  promo_code_name: yup
     .string()
     .matches(
       /^[a-zA-Z0-9@!_-]+(?: [a-zA-Z0-9@!_-]+)*$/,
       () => <IntlMessages id="validation.promoCodeName.pattern" />
     )
     .max(100, () => <IntlMessages id="validation.promoCodeName.maxLength" />)
     .required(<IntlMessages id='validation.promonameRequired' />),
   code: yup
     .string()
     .matches(
       /^[a-zA-Z0-9@!_-]+(?: [a-zA-Z0-9@!_-]+)*$/,
       () => <IntlMessages id="validation.code.pattern" />
     )
     .required(<IntlMessages id='validation.promocodeRequired' />)
    .max(20,  () => <IntlMessages id='validation.promoCodeTooLong' />)
    .matches(/^\S*$/, () => <IntlMessages id='validation.promoCodeNoSpaces' />),
  start_date: yup.date().typeError(<IntlMessages id='validation.dateInvalid' />).nullable(),
  end_date: yup
    .date()
    .typeError(<IntlMessages id='validation.dateInvalid' />)
    .nullable()
    .test({
      name: 'greaterThanStart',
      exclusive: false,
      message: <IntlMessages id='validation.endDateBeforeStart' />,
      test: function (value, { parent }) {
        const startDate = new Date(parent.start_date);
        return !value || !startDate || new Date(value) >= new Date(startDate);
      },
    })
    .test({
      name: 'greaterThanCurrentDate',
      exclusive: false,
      message: <IntlMessages id='validation.endDateBeforeCurrent' />,
      test: function (value) {
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        const inputDate = new Date(value);
        inputDate.setHours(0, 0, 0, 0);

        return !value || inputDate >= currentDate;
      },
    }),
  usage_limit: yup.number().typeError(<IntlMessages id='validation.usageLimitInvalid' />).nullable(),
    discount_value: yup
      .number()
      .typeError(<IntlMessages id='validation.discountValueInvalid' />)
      .required(<IntlMessages id='validation.discountValueRequired' />)
      .min(0, <IntlMessages id='validation.discountValueTooLow' />)
      .max(9999999.99, <IntlMessages id='validation.discountValueTooHigh' />)
      .test({
        name: 'maxPercentage',
        test: function (value) {//NOSONAR
          const discountType = this.resolve(yup.ref('discount_type'));
          const isPercentage = discountType === 'percentage'; // NOSONAR: Remove this "===" check; it will always be false.

          if (isPercentage && value > 100) {
            return this.createError({
              path: this.path,
              message: <IntlMessages id='validation.discountPercentageTooHigh' />,
            });
          }

          return true;
        },
      }),
    discount_type: yup
      .string()
      .required(<IntlMessages id='validation.promotionTypeRequired' />)
      .oneOf(['percentage', 'fixed'], <IntlMessages id='validation.promotionTypeInvalid' />),
    product_id: yup.string().when('service_type', {
      is: 'product',
      then: (schema) =>
        schema.required(<IntlMessages id='validation.productRequired' />),
      otherwise: (schema) => schema.nullable(),
    }),

    tournament_id: yup.string().when('service_type', {
      is: 'tournament',
      then: (schema) =>
        schema.required(<IntlMessages id='validation.tournamentRequired' />),
      otherwise: (schema) => schema.nullable(),
    }),
});

const renderServiceType = ({
  serviceType,
  selectedProductOption,
  productOptions,
  setSelectedTournamentOption,
  setSelectedProductOption,
  verbiageSettings,
  touched,
  errors,
  tournamentId,
  selectedTournamentOption,
  tournamentOptions,
  setFieldValue,
}) => {
  return (
    <>
      {serviceType === 'product' && (
        <Grid size={{ xs: 12, md: 6 }}>
          <Autocomplete
            sx={{
              width: '100%',
            }}
            value={selectedProductOption}
            options={[
              ...(productOptions || []).sort((a, b) =>
                a.product_name.localeCompare(b.product_name, undefined, {
                  numeric: true,
                  sensitivity: 'base',
                }),
              ),
            ]}
            getOptionLabel={(option) =>
              option.badge
                ? `${option.product_name} - ${option.badge}`
                : option.product_name
            }
            onChange={(event, newValue) => {
              setSelectedProductOption(newValue);
              setSelectedTournamentOption(null);
              setFieldValue("product_id", newValue ? String(newValue.id) : '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={verbiageSettings.product_type || 'Products'}
                error={touched.product_id && Boolean(errors.product_id)}
                helperText={
                  touched.product_id && errors.product_id
                    ? 'Please choose a product.'
                    : ''
                }
              />
            )}
          />
        </Grid>
      )}
      {serviceType === 'tournament' && !tournamentId && (
        <Grid size={{ xs: 12, md: 6 }}>
          <Autocomplete
            sx={{
              width: '100%',
            }}
            value={selectedTournamentOption}
            options={[
              ...(tournamentOptions || []).sort((a, b) =>
                a.tournament_name.localeCompare(b.tournament_name, undefined, {
                  numeric: true,
                  sensitivity: 'base',
                }),
              ),
            ]}
            getOptionLabel={(option) => {
              return option.tournament_name;
            }}
            onChange={(event, newValue) => {
              setSelectedTournamentOption(newValue);
              setSelectedProductOption(null);
              setFieldValue("tournament_id", newValue ? newValue.id : null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={'Tournaments'}
                error={touched.tournament_id && Boolean(errors.tournament_id)}
                helperText={
                  touched.tournament_id && errors.tournament_id
                    ? 'Please choose a tournament.'
                    : ''
                }
              />
            )}
          />
        </Grid>
      )}
    </>
  );
};
const renderDates = ({ values, setFieldValue, touched, setErrors, errors }) => {
  return (
    <>
      <Grid size={{ xs: 12, md: 6 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack spacing={1}>
            <DatePicker
              // minDate={new Date(values.start_date)}
              minDate={
                new Date(values.start_date) > new Date()
                  ? new Date()
                  : new Date(values.start_date)
              }
              label='Start Date'
              name='start_date'
              value={values.start_date ? new Date(values.start_date) : null}
              onChange={(date) => {
                if (date) {
                  setFieldValue('start_date', new Date(date).toUTCString());

                  touched.start_date = true;
                } else {
                  setFieldValue('start_date', null);
                }
              }}
              onError={(newError) => setErrors({ start_date: newError })}
            />
            <FormHelperText style={{ color: '#d32f2f', marginLeft: '15px' }}>
              {touched.start_date && errors.start_date}
            </FormHelperText>
          </Stack>
        </LocalizationProvider>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack spacing={1}>
            <DatePicker
              disablePast
              label='End Date'
              value={values.end_date ? new Date(values.end_date) : null}
              onChange={(date) => {
                if (date) {
                  setFieldValue('end_date', new Date(date).toUTCString());

                  touched.end_date = true;
                } else {
                  setFieldValue('end_date', null);
                }
              }}
              name='end_date'
              onError={(newError) => setErrors({ end_date: newError })}
            />
            <FormHelperText style={{ color: '#d32f2f', marginLeft: '15px' }}>
              {touched.end_date && errors.end_date !== 'disablePast'
                ? errors.end_date
                : null}
            </FormHelperText>
          </Stack>
        </LocalizationProvider>
      </Grid>
    </>
  );
};
// Here we are tournamentId so we can know that is from tournament settings
const EditPromoCode = ({
  isOpen,
  onClose,
  promoCodeData,
  fetchData,
  verbiageSettings,
  tournamentId,
}) => {
  const companyTheme = CompanyTheme();
  const infoViewActionsContext = useInfoViewActionsContext();
  const isMobile = useMediaQuery('(max-width:755px)');
  const [selectedProductOption, setSelectedProductOption] = useState(
    promoCodeData.product_id
      ? {
          id: promoCodeData.product_id,
          product_name: promoCodeData?.product_name,
          badge: promoCodeData?.badge,
        }
      : null
  );
  const searchProduct = '';
  const [productOptions, setProductOptions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [tournamentOptions, setTournamentOptions] = useState([]);
  const [selectedTournamentOption, setSelectedTournamentOption] = useState(
    promoCodeData.tournament_id
      ? {
          id: promoCodeData.tournament_id,
          tournament_name: promoCodeData?.tournament_name,
        }
      : null
  );
  const getServiceType = () => {
    if (promoCodeData.product_id) return 'product';
    if (promoCodeData.tournament_id) return 'tournament';
    return '';
  };

  const [serviceType, setServiceType] = useState(getServiceType());
  useEffect(() => {
    fetchProductData();
  }, [searchProduct]);

  const fetchProductData = async () => {
    try {
      const response = await jwtAxios.get(`/products`, {
        params: {
          perPage: 'all',
          search: searchProduct,
        },
      });
      const { data } = response;
      setProductOptions(data.list);
    } catch (error) {
      setProductOptions([]);
    }
  };
  const handleDialogOpen = () => {
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const handleCloseModalDialog = () => {
    setOpenDialog(false); // to close the dialog
    handleModalClose(); //to close the modal
  };
  const handleModalClose = () => {
    onClose();
  };
  const fetchTournamentsData = async () => {
    try {
      const response = await jwtAxios.get('/tournament');

      setTournamentOptions(response.data.list);
    } catch (error) {
      setTournamentOptions([]);
    }
  };
  useEffect(() => {
    fetchTournamentsData();
  }, []);
  return (
    <>
      <Modal open={isOpen} onClose={handleDialogOpen}>
        <Box
          className='p-popup'
          sx={{
            bgcolor: 'background.paper',
          }}
        >
          <Formik
            initialValues={{
              promo_code_name: promoCodeData?.promo_code_name || '',
              code: promoCodeData?.code || '',
              start_date: promoCodeData?.start_date
                ? `${promoCodeData.start_date.substring(0, 10)}T12:01:00`
                : null,
              end_date: promoCodeData?.end_date
                ? `${promoCodeData.end_date.substring(0, 10)}T12:01:00`
                : null,
              usage_limit: promoCodeData?.usage_limit || null,
              discount_type: promoCodeData?.discount_type || '',
              discount_value: promoCodeData?.discount_value || '',
              product_id:promoCodeData.product_id || null,
              tournament_id:promoCodeData.tournament_id || null,
              service_type: getServiceType() || '',
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              values.start_date = values.start_date
                ? format(new Date(values.start_date), 'yyyy-MM-dd')
                : null;
              values.end_date = values.end_date
                ? format(new Date(values.end_date), 'yyyy-MM-dd')
                : null;
                const { service_type, ...restValues } = values;
              const requestData = {
                ...restValues,
                start_date: values.start_date,
                end_date: values.end_date,
                product_id:
                  selectedProductOption && serviceType === 'product'
                    ? selectedProductOption.id
                    : null,
                tournament_id:
                  selectedTournamentOption && serviceType === 'tournament'
                    ? selectedTournamentOption.id
                    : null,
              };
              setSubmitting(true);
              try {
                await jwtAxios.put(
                  `/promo-codes/${promoCodeData.id}`,
                  requestData,
                );
                setSubmitting(false);
                handleCloseModalDialog();
                fetchData();
                infoViewActionsContext.showMessage(
                  'Promo Code updated successfully!',
                );
              } catch (error) {
                infoViewActionsContext.fetchError(error.response.data.error);
                console.error('Error updating promo code:', error);
                setSubmitting(false);
              }
            }}
          >
            {({
              setFieldValue,
              values,
              handleSubmit,
              handleChange,
              isSubmitting,
              touched,
              errors,
              setErrors,
            }) => (
              <Form noValidate autoComplete='off'>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography
                      variant='h4'
                      sx={{
                        color: companyTheme
                          ? companyTheme.primary.main
                          : 'text.primary',
                      }}
                    >
                      Edit Promo Code
                    </Typography>
                    <Typography
                      variant='body1'
                      sx={{ mt: 2, color: 'text.secondary' }}
                    >
                      All promo codes at company level apply to all shopping
                      cart purchases.
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton
                      size='small'
                      sx={{ fontWeight: 'bold', color: '#000000' }}
                      className='icon-btn'
                      onClick={handleDialogOpen}
                    >
                      <CloseRounded />
                    </IconButton>
                  </Box>
                </Box>
                <AppGridContainer sx={{ mt: 2 }}>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <Box
                      sx={{
                        border: '1px solid #EAECF0',
                        p: 6,
                        borderRadius: 3,
                        boxShadow:
                          '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                      }}
                    >
                      <AppGridContainer>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <AppTextField
                            name='promo_code_name'
                            variant='outlined'
                            sx={{
                              width: '100%',
                              my: 2,
                            }}
                            label='Name'
                            inputProps={{ maxLength: 101 }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <AppTextField
                            name='code'
                            variant='outlined'
                            sx={{
                              width: '100%',
                              my: 2,
                            }}
                            label='Promo code'
                            inputProps={{ maxLength: 21 }}
                          />
                        </Grid>
                        {!tournamentId && (
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              sx={{ width: '100%' }}
                              select
                              name='service_type'
                              labelId='discount-type-select-label'
                              label='Service Type'
                              id='service-type-select'
                              value={serviceType}
                              onChange={(e) => {
                                setFieldValue('service_type', e.target.value);
                                // optionally also update state if needed
                                setServiceType(e.target.value);
                                if (e.target.value !== 'product') setSelectedProductOption(null);
                                if (e.target.value !== 'tournament') setSelectedTournamentOption(null);
                              }}
                            >
                              <MenuItem value=''>None</MenuItem>
                              <MenuItem value='product'>Products</MenuItem>
                              <MenuItem value='tournament'>
                                Tournaments
                              </MenuItem>
                            </TextField>
                          </Grid>
                        )}
                        {renderServiceType({
                          errors,
                          productOptions,
                          selectedProductOption,
                          selectedTournamentOption,
                          serviceType,
                          setSelectedProductOption,
                          setSelectedTournamentOption,
                          touched,
                          tournamentId,
                          tournamentOptions,
                          verbiageSettings,
                          setFieldValue,
                        })}
                        {renderDates({
                          errors,
                          setErrors,
                          setFieldValue,
                          touched,
                          values,
                        })}
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            name='usage_limit'
                            variant='outlined'
                            value={values.usage_limit}
                            error={
                              touched.usage_limit && Boolean(errors.usage_limit)
                            }
                            // helperText={touched.usage_limit && errors.usage_limit}
                            helperText={
                              (touched.usage_limit && errors.usage_limit) ||
                              'Specify the maximum number of times this promocode can be used.'
                            }
                            onChange={(event) => {
                              setFieldValue(
                                'usage_limit',
                                event.target.value === ''
                                  ? null
                                  : event.target.value,
                              );
                            }}
                            sx={{
                              width: '100%',
                            }}
                            label='Limit'
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <FormControl fullWidth>
                            <InputLabel id='discount-type-select-label'>
                              Promotion Type
                            </InputLabel>
                            <Select
                              name='discount_type'
                              labelId='discount-type-select-label'
                              id='discount-type-select'
                              value={values.discount_type}
                              label='Promotion Type'
                              onChange={handleChange}
                            >
                              <MenuItem value='fixed'>Fixed</MenuItem>
                              <MenuItem value='percentage'>Percentage</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <AppTextField
                            name='discount_value'
                            variant='outlined'
                            sx={{
                              width: '100%',
                            }}
                            label='Value'
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 12 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Button
                              sx={{
                                position: 'relative',
                                minWidth: 100,
                                backgroundColor: companyTheme
                                  ? companyTheme.primary.main
                                  : '#2196f3',
                                '&:hover': {
                                  backgroundColor: companyTheme
                                    ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                                    : alpha('#2196f3', 0.8),
                                },
                              }}
                              variant='contained'
                              type='submit'
                              size={isMobile ? 'small' : 'medium'}
                            >
                              {isMobile ? (
                                <IntlMessages id='common.save' />
                              ) : (
                                <IntlMessages id='common.saveChanges' />
                              )}
                            </Button>
                            <Button
                              sx={{
                                position: 'relative',
                                minWidth: 100,
                                ml: 2.5,
                                borderColor: companyTheme
                                  ? companyTheme.primary.main
                                  : '#2196f3',
                                '&:hover': {
                                  borderColor: companyTheme
                                    ? alpha(companyTheme.primary.main, 0.8)
                                    : alpha('#2196f3', 0.8),
                                },
                                color: companyTheme
                                  ? companyTheme.primary.main
                                  : '#2196f3',
                              }}
                              variant='outlined'
                              onClick={handleDialogOpen}
                              size={isMobile ? 'small' : 'medium'}
                            >
                              <IntlMessages id='common.cancel' />
                            </Button>
                          </Box>
                        </Grid>
                      </AppGridContainer>
                    </Box>
                  </Grid>
                </AppGridContainer>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
      <AppModalConfirmation
        openDialog={openDialog}
        handleCloseDialog={handleCloseDialog}
        handleCloseModalDialog={handleCloseModalDialog}
      />
    </>
  );
};

export default EditPromoCode;
EditPromoCode.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  promoCodeData: PropTypes.object,
  fetchData: PropTypes.func,
  verbiageSettings: PropTypes.object,
  tournamentId: PropTypes.number,
};
